import * as XLSX from "xlsx";

export type RowObject = Record<string, any>;

// Required columns for inventory upload (case-insensitive)
const REQUIRED_COLUMNS = [
  "Manufacturer",
  "PartNumber",
  "Model",
  "Quantity",
  "Condition",
  "Price",
  "Category",
  "Description",
];

export interface FileFormatValidationResult {
  isValid: boolean;
  missingColumns: string[];
  foundColumns: string[];
  errorMessage: string;
}

/**
 * Normalize column name to handle variations
 */
function normalizeColumnName(columnName: string): string {
  if (!columnName) return "";

  // Trim whitespace and convert to lowercase for comparison
  const normalized = columnName.trim().toLowerCase();

  // Handle PartNumber variations
  if (
    normalized === "partnumber" ||
    normalized === "part number" ||
    normalized === "part_number" ||
    normalized === "partnum"
  ) {
    return "PartNumber";
  }

  // Handle Manufacturer variations
  if (normalized === "manufactu" || normalized.startsWith("manufactur")) {
    return "Manufacturer";
  }

  // For other columns, check case-insensitive match
  for (const requiredCol of REQUIRED_COLUMNS) {
    if (normalized === requiredCol.toLowerCase()) {
      return requiredCol;
    }
  }

  return columnName.trim();
}

/**
 * Validate file format - check for required columns
 */
export function validateFileFormat(headers: string[]): FileFormatValidationResult {
  const normalizedHeaders = headers.map(normalizeColumnName);
  const foundColumns: string[] = [];
  const missingColumns: string[] = [];

  // Check each required column
  for (const requiredCol of REQUIRED_COLUMNS) {
    const found = normalizedHeaders.some((h) => h.toLowerCase() === requiredCol.toLowerCase());
    if (found) {
      foundColumns.push(requiredCol);
    } else {
      missingColumns.push(requiredCol);
    }
  }

  const isValid = missingColumns.length === 0;

  let errorMessage = "";
  if (!isValid) {
    errorMessage = `Your file is missing required columns. Your Excel or CSV file must have these column names in the first row: ${REQUIRED_COLUMNS.join(", ")}. Missing columns: ${missingColumns.join(", ")}. Please add these columns to your file and try uploading again.`;
  }

  return {
    isValid,
    missingColumns,
    foundColumns,
    errorMessage,
  };
}

export function parseFileToRows(file: File): Promise<RowObject[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      try {
        const data = new Uint8Array(reader.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];

        // First, get headers from first row
        const headerRow = XLSX.utils.sheet_to_json(ws, {
          defval: null,
          header: 1,
          range: 0,
        })[0] as string[];

        if (!headerRow || headerRow.length === 0) {
          reject(
            new Error(
              "Your file doesn't have a header row. Please make sure the first row contains column names.",
            ),
          );
          return;
        }

        // Validate file format
        const validation = validateFileFormat(headerRow);
        if (!validation.isValid) {
          reject(new Error(validation.errorMessage));
          return;
        }

        // Parse rows with proper headers
        const rows = XLSX.utils.sheet_to_json(ws, { defval: null });
        resolve(rows as RowObject[]);
      } catch (error) {
        if (error instanceof Error) {
          reject(error);
        } else {
          reject(
            new Error("Failed to parse file. Please make sure it's a valid Excel or CSV file."),
          );
        }
      }
    };

    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

export function chunkRows(rows: RowObject[], size = 1000): RowObject[][] {
  const out: RowObject[][] = [];
  for (let i = 0; i < rows.length; i += size) {
    out.push(rows.slice(i, i + size));
  }
  return out;
}
